import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Save, Edit3, Star, Award, X, Plus, MapPin } from 'lucide-react';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import BrandImagePicker from '../branding/BrandImagePicker';
import { useUpdateBusinessProfile, useVendorProfile, vendorProfileKeys } from '../../../services/vendorProfileService';
import { vendorBrandingKeys } from '../../../services/vendorBrandingService';
import { useToastContext } from '../../../contexts/ToastContext';
import useGeolocation from '../../../hooks/useGeolocation';
import { normalizeDetectedCity, formatLocationDisplay } from '../../../utils/userLocationDisplay';

const BusinessProfileTab = ({ user, updateUser, readOnly = false }) => {
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState('');
  
  // Fetch vendor profile data directly from API
  const { data: vendorProfileResponse, isLoading: profileLoading } = useVendorProfile();
  const vendorProfile = vendorProfileResponse?.data || user?.vendor_profile;
  
  const [businessData, setBusinessData] = useState({
    business_name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    latitude: '',
    longitude: '',
    years_experience: '',
    specialties: [],
    insurance_info: '',
    business_license: '',
    logo_url: '',
    average_rating: 0,
    total_reviews: 0,
    is_featured: false,
    is_verified: false
  });

  const [businessErrors, setBusinessErrors] = useState({});

  const {
    getCurrentLocation,
    loading: geoLoading,
  } = useGeolocation();

  const handleAutofillCurrentLocation = async () => {
    try {
      const detected = await getCurrentLocation({ preferIp: true });
      if (detected) {
        const country = detected.country || detected.countryCode || '';
        const rawCity = detected.city || '';
        const city = normalizeDetectedCity({ city: rawCity, state: detected.state, country }) || rawCity;
        
        setBusinessData(prev => ({
          ...prev,
          country: country || prev.country,
          city: city || prev.city,
          state: detected.state || prev.state,
          address: detected.address || prev.address,
          latitude: detected.latitude != null ? detected.latitude : prev.latitude,
          longitude: detected.longitude != null ? detected.longitude : prev.longitude,
        }));
        
        const display = formatLocationDisplay({ city, state: detected.state, country });
        toast.info('Location pre-filled', `Filled with detected location (${display || country}). Remember to save changes.`);
      } else {
        toast.error('Location detection', 'Could not detect current location.');
      }
    } catch {
      toast.error('Location detection', 'Failed to retrieve current location.');
    }
  };

  useEffect(() => {
    if (vendorProfile) {
      // Ensure specialties is always an array - handle both JSON string and array
      let specialties = vendorProfile.specialties || [];
      if (typeof specialties === 'string') {
        try {
          specialties = JSON.parse(specialties);
        } catch (e) {
          console.error('Error parsing specialties:', e);
          specialties = [];
        }
      }
      const specialtiesArray = Array.isArray(specialties) ? specialties : [];
      
      setBusinessData({
        business_name: vendorProfile.business_name || '',
        description: vendorProfile.description || '',
        address: vendorProfile.address || '',
        city: vendorProfile.city || '',
        state: vendorProfile.state || '',
        zip_code: vendorProfile.zip_code || '',
        country: vendorProfile.country || 'US',
        latitude: vendorProfile.latitude || '',
        longitude: vendorProfile.longitude || '',
        years_experience: vendorProfile.years_experience || '',
        specialties: specialtiesArray,
        insurance_info: vendorProfile.insurance_info || '',
        business_license: vendorProfile.business_license || '',
        logo_url: vendorProfile.logo_url || '',
        average_rating: vendorProfile.average_rating || 0,
        total_reviews: vendorProfile.total_reviews || 0,
        is_featured: vendorProfile.is_featured || false,
        is_verified: vendorProfile.is_verified || false
      });
    }
  }, [vendorProfile]);

  const handleBusinessInputChange = (field, value) => {
    setBusinessData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (businessErrors[field]) {
      setBusinessErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateBusinessForm = () => {
    const newErrors = {};
    
    if (!businessData.business_name.trim()) newErrors.business_name = 'Business name is required';
    if (!businessData.description.trim()) newErrors.description = 'Business description is required';
    if (!businessData.address.trim()) newErrors.address = 'Business address is required';
    if (!businessData.city.trim()) newErrors.city = 'City is required';
    if (!businessData.state.trim()) newErrors.state = 'State is required';
    if (!businessData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
    if (businessData.years_experience && businessData.years_experience < 0) {
      newErrors.years_experience = 'Years of experience must be 0 or greater';
    }
    
    setBusinessErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSpecialty = () => {
    if (newSpecialty.trim() && !businessData.specialties.includes(newSpecialty.trim())) {
      setBusinessData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty.trim()]
      }));
      setNewSpecialty('');
    }
  };

  const handleRemoveSpecialty = (specialtyToRemove) => {
    setBusinessData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialtyToRemove)
    }));
  };

  const handleSpecialtyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSpecialty();
    }
  };

  const updateBusinessProfileMutation = useUpdateBusinessProfile({
    onSuccess: (result) => {
      // Update local state immediately
      if (result.data) {
        // Update businessData with the response data
        setBusinessData(prev => ({
          ...prev,
          ...result.data,
          logo_url: result.data.logo_url || '',
          specialties: result.data.specialties ? 
            (typeof result.data.specialties === 'string' ? JSON.parse(result.data.specialties) : result.data.specialties) 
            : prev.specialties
        }));
        
        // Update user context with merged vendor_profile
        const updatedUser = { 
          ...user,
          vendor_profile: {
            ...(user?.vendor_profile || {}),
            ...result.data
          }
        };
        updateUser(updatedUser);
      }
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
      queryClient.invalidateQueries({ queryKey: vendorBrandingKeys.settings() });
      setIsEditing(false);
      toast.success('Business Profile Updated', 'Your business profile has been updated successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update business profile';
      const errorDetails = error?.response?.data?.errors;
      
      // Handle validation errors
      if (errorDetails) {
        const fieldErrors = {};
        Object.keys(errorDetails).forEach(key => {
          fieldErrors[key] = errorDetails[key][0];
        });
        setBusinessErrors(fieldErrors);
        toast.error('Validation Error', Object.values(fieldErrors)[0] || errorMessage);
      } else {
        toast.error('Update Failed', errorMessage);
      }
      console.error('Update failed:', errorMessage);
    }
  });

  const handleUpdateBusinessProfile = () => {
    if (!validateBusinessForm()) return;
    updateBusinessProfileMutation.mutate({
      ...businessData,
      logo_url: businessData.logo_url || null,
    });
  };

  if (profileLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">Business Profile</h3>
        {!readOnly && (
          !isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              size="sm"
              className="flex items-center w-full sm:w-auto justify-center"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Business Profile
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <Button
                onClick={() => {
                  if (vendorProfile) {
                    setBusinessData((prev) => ({
                      ...prev,
                      logo_url: vendorProfile.logo_url || '',
                    }));
                  }
                  setIsEditing(false);
                }}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateBusinessProfile}
                disabled={updateBusinessProfileMutation.isPending}
                size="sm"
                className="flex items-center justify-center w-full sm:w-auto"
              >
                {updateBusinessProfileMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )
        )}
      </div>

      {/* Business logo — separate from personal profile photo */}
      <div className="pb-4 sm:pb-6 border-b border-gray-200">
        <div className="mb-3">
          <h4 className="text-sm font-medium text-gray-900">Business logo</h4>
          <p className="text-xs text-gray-500 mt-1">
            Used on invoices/receipts and custom branding. Separate from your profile photo.
            {!isEditing && !readOnly && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="text-primary-600 hover:underline font-medium"
                >
                  Edit to change
                </button>
              </>
            )}
          </p>
        </div>
        {isEditing && !readOnly ? (
          <BrandImagePicker
            label="Logo"
            value={businessData.logo_url || null}
            onChange={(url) => handleBusinessInputChange('logo_url', url || '')}
            hint="Square logos work best. Shown on receipts and public branding."
            aspectClass="aspect-square max-w-[120px]"
            uploadImageType="general"
            showGallery={false}
          />
        ) : (
          <div className="flex items-center gap-4">
            {businessData.logo_url ? (
              <img
                src={businessData.logo_url}
                alt="Business logo"
                className="w-20 h-20 rounded-lg object-cover border border-gray-200 bg-white"
              />
            ) : (
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-400 text-center px-2">
                No logo
              </div>
            )}
            <div className="text-sm text-gray-600">
              {businessData.logo_url ? (
                <p>This logo appears on receipts and custom branding automatically.</p>
              ) : (
                <p>
                  No logo yet. Upload one here and it will show on{' '}
                  {/* <Link to="/vendor/custom-branding" className="text-primary-600 hover:underline"> */}
                    custom branding
                  {/* </Link>{' '} */}
                  and invoices.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Business Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${businessData.is_verified ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Verification Status</p>
              <p className="text-sm text-gray-600 truncate">
                {businessData.is_verified ? 'Verified' : 'Pending Verification'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <Star className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Average Rating</p>
              <p className="text-sm text-gray-600 truncate">
                {businessData.average_rating ? `${businessData.average_rating}/5.0` : 'No ratings yet'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <Award className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Total Reviews</p>
              <p className="text-sm text-gray-600">{businessData.total_reviews}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {readOnly ? (
          <>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <p className="text-sm text-gray-900">{businessData.business_name || 'N/A'}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{businessData.description || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
              <p className="text-sm text-gray-900">{businessData.address || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <p className="text-sm text-gray-900">{businessData.city || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <p className="text-sm text-gray-900">{businessData.state || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
              <p className="text-sm text-gray-900">{businessData.zip_code || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <p className="text-sm text-gray-900">{businessData.country || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
              <p className="text-sm text-gray-900">{businessData.years_experience || 'N/A'}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {businessData.specialties && businessData.specialties.length > 0 ? (
                  businessData.specialties.map((specialty, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {specialty}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No specialties added</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Information</label>
              <p className="text-sm text-gray-900">{businessData.insurance_info || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business License</label>
              <p className="text-sm text-gray-900">{businessData.business_license || 'N/A'}</p>
            </div>
          </>
        ) : (
          <>
            <div className="sm:col-span-2">
              <Input
                label="Business Name"
                value={businessData.business_name}
                onChange={(e) => handleBusinessInputChange('business_name', e.target.value)}
                error={businessErrors.business_name}
                disabled={!isEditing}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Description
              </label>
              <textarea
                value={businessData.description}
                onChange={(e) => handleBusinessInputChange('description', e.target.value)}
                disabled={!isEditing}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  businessErrors.description ? 'border-red-300' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50' : 'bg-white'}`}
                placeholder="Describe your business, services, and what makes you unique..."
              />
              {businessErrors.description && (
                <p className="mt-1 text-sm text-red-600">{businessErrors.description}</p>
              )}
            </div>

            <div className="sm:col-span-2 pt-2 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Operating Address & Region</h4>
                <p className="text-xs text-gray-500">Your primary business location used for client search and bookings.</p>
              </div>
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={handleAutofillCurrentLocation}
                  disabled={geoLoading}
                  className="text-xs self-start sm:self-auto border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <MapPin className={`w-3.5 h-3.5 mr-1.5 ${geoLoading ? 'animate-spin text-primary-600' : 'text-primary-500'}`} />
                  {geoLoading ? 'Detecting Location...' : 'Use Current Live Location'}
                </Button>
              )}
            </div>

            <Input
              label="Business Address"
              value={businessData.address}
              onChange={(e) => handleBusinessInputChange('address', e.target.value)}
              error={businessErrors.address}
              disabled={!isEditing}
              required
            />

            <Input
              label="City"
              value={businessData.city}
              onChange={(e) => handleBusinessInputChange('city', e.target.value)}
              error={businessErrors.city}
              disabled={!isEditing}
              required
            />

            <Input
              label="State"
              value={businessData.state}
              onChange={(e) => handleBusinessInputChange('state', e.target.value)}
              error={businessErrors.state}
              disabled={!isEditing}
              required
            />

            <Input
              label="ZIP Code"
              value={businessData.zip_code}
              onChange={(e) => handleBusinessInputChange('zip_code', e.target.value)}
              error={businessErrors.zip_code}
              disabled={!isEditing}
              required
            />

            <Input
              label="Country"
              value={businessData.country}
              onChange={(e) => handleBusinessInputChange('country', e.target.value)}
              error={businessErrors.country}
              disabled={!isEditing}
            />

            <Input
              label="Years of Experience"
              type="number"
              value={businessData.years_experience}
              onChange={(e) => handleBusinessInputChange('years_experience', e.target.value)}
              error={businessErrors.years_experience}
              disabled={!isEditing}
            />

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialties
              </label>
              
              {/* Display existing specialties as tags */}
              {businessData.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {businessData.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                    >
                      {specialty}
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecialty(specialty)}
                          className="ml-1 text-gray-500 hover:text-red-600 transition-colors"
                          aria-label={`Remove ${specialty}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Add new specialty input */}
              {isEditing && (
                <div className="flex gap-2">
                  <Input
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    onKeyPress={handleSpecialtyKeyPress}
                    placeholder="Add a specialty (e.g., Weddings, Corporate Events)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddSpecialty}
                    variant="outline"
                    size="sm"
                    className="flex items-center"
                    disabled={!newSpecialty.trim()}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              )}

              {!isEditing && businessData.specialties.length === 0 && (
                <p className="text-sm text-gray-500">No specialties added yet</p>
              )}
            </div>

            <Input
              label="Insurance Information"
              value={businessData.insurance_info}
              onChange={(e) => handleBusinessInputChange('insurance_info', e.target.value)}
              error={businessErrors.insurance_info}
              disabled={!isEditing}
              placeholder="Insurance provider and policy details"
            />

            <Input
              label="Business License"
              value={businessData.business_license}
              onChange={(e) => handleBusinessInputChange('business_license', e.target.value)}
              error={businessErrors.business_license}
              disabled={!isEditing}
              placeholder="Business license number"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BusinessProfileTab;

